import { z } from 'zod';

/**
 * Zod schema for automation form validation
 * Matches backend API structure requirements
 */
export const automationFormSchema = z
    .object({
        // Trigger configuration
        trigger_source: z.enum(['comment', 'message', 'all'], {
            required_error: 'Please select a trigger type',
        }),

        // Post selection (optional - if empty, triggers on all posts)
        media_ids: z.array(z.string()),

        // Keywords (stored as comma-separated string in backend)
        keywords: z
            .array(z.string().min(1, 'Keyword cannot be empty'))
            .min(1, 'At least 1 keyword required')
            .max(5, 'Maximum 5 keywords allowed'),

        // Comment reply (optional - array of choices, backend randomly selects one)
        comment_choices: z
            .array(z.string().max(1000, 'Comment reply too long (max 1000 characters)'))
            .max(3, 'Maximum 3 comment reply variations allowed'),

        // Auto-DM message content (required)
        content: z.string().min(1, 'Auto-DM message is required').max(1000, 'Message too long (max 1000 characters)'),

        // Link button (optional)
        buttons: z
            .array(
                z.object({
                    url: z.string().url('Invalid URL format'),
                    title: z
                        .string()
                        .min(1, 'Button title required')
                        .max(30, 'Button title too long (max 30 characters)'),
                    type: z.literal('web_url'),
                })
            )
            .max(1),

        // Status
        status: z.enum(['draft', 'published']),
    })
    .refine(
        data => {
            // Only validate comment_choices when trigger_source is 'comment' or 'all'
            if (data.trigger_source === 'message') {
                return true;
            }
            // If comment replies are provided, at least one must have content
            if (data.comment_choices.length > 0) {
                return data.comment_choices.some(choice => choice.trim().length > 0);
            }
            // Empty comment_choices is valid (means "Do not comment")
            return true;
        },
        {
            message: 'At least 1 comment reply is required',
            path: ['comment_choices'],
        }
    );

export type AutomationFormData = z.infer<typeof automationFormSchema>;

/**
 * Default form values for new automation
 */
export const getDefaultAutomationFormValues = (): AutomationFormData => ({
    trigger_source: 'all',
    media_ids: [],
    keywords: ['link'],
    comment_choices: [],
    content: '',
    buttons: [],
    status: 'draft',
});
